"use client";

import { useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import { storyblokEditable, type SbBlokData } from "@storyblok/react/rsc";

import { useTriggerAction, type BlokAction } from "@/lib/action-link";
import { resolveBg } from "@/lib/bg-color";
import { regionFromPathname } from "@/lib/region";
import { resolveLink, type StoryblokLink } from "@/lib/storyblok-link";
import { useMediaQuery } from "@/lib/use-media-query";

type GraphRow = SbBlokData & {
  label?: string;
  baseline_value?: string | number;
  baseline_suffix?: string;
  bar_value?: string | number;
  bar_suffix?: string;
};

type GraphDataStory = {
  content?: { rows?: GraphRow[] };
};

type SuccessGraphsBlok = SbBlokData & {
  title?: string;
  description?: string;
  button_label?: string;
  button_link?: StoryblokLink;
  button_action?: BlokAction | "";
  bg_color?: unknown;
  bar_color?: unknown;
  chart_position?: "left" | "right";
  rows_data?: string | GraphDataStory;
  baseline_label?: string;
  bar_label?: string;
  footnote?: string;
};

function ArrowIcon() {
  return (
    <svg width="17" height="11" viewBox="0 0 17 11" fill="none" aria-hidden>
      <path
        d="M1 5.5h14.5M11 1l4.5 4.5L11 10"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function parseNum(v: string | number | undefined): number {
  if (v === undefined || v === null || v === "") return 0;
  const n = typeof v === "number" ? v : parseFloat(v);
  return Number.isFinite(n) ? n : 0;
}

function format(n: number, suffix?: string): string {
  // Storyblok returns empty fields as "", so treat a blank suffix as
  // unset and fall back to "%".
  const trimmed = suffix?.trim();
  return `${n}${trimmed || "%"}`;
}

export default function SuccessGraphs({ blok }: { blok: SuccessGraphsBlok }) {
  const { bg } = resolveBg(blok.bg_color, "dusk-01");
  const { bg: barBgClass } = resolveBg(blok.bar_color, "purple-03");
  const region = regionFromPathname(usePathname());
  const link = resolveLink(blok.button_link, region);
  const triggerAction = useTriggerAction();
  const action = blok.button_action || null;
  const showButton = Boolean(blok.button_label && (action || link.href !== "#"));

  // `rows_data` references a `graph_data` story: a full object once
  // resolved, a bare UUID string before (renders nothing).
  const resolvedRows =
    typeof blok.rows_data === "object" && blok.rows_data
      ? (blok.rows_data.content?.rows ?? [])
      : [];
  const rows = resolvedRows.filter((r) => r.label || r.bar_value || r.baseline_value);

  // Scale so the longest bar fills ~80%, leaving headroom for the label.
  const max = rows.reduce(
    (m, r) => Math.max(m, parseNum(r.baseline_value), parseNum(r.bar_value)),
    0,
  );
  const scale = Math.max(50, max * 1.25);

  // DOM order is text → chart (mobile stacking); chart-on-left desktop
  // layout flips via flex-row-reverse.
  const chartOnLeft = blok.chart_position !== "right";

  // Legend describes the two bar series; labels are editable with the
  // design defaults. Footnote is optional small print under the legend.
  const baselineLabel = blok.baseline_label?.trim() || "HEFA Average";
  const barLabel =
    blok.bar_label?.trim() || "Thérapie Fertility Clinical Pregnancy Rate Jan ’22 – Dec ’24";
  const footnote = blok.footnote?.trim();

  const buttonClass =
    "inline-flex w-fit items-center gap-[7px] py-2.5 text-body font-extrabold leading-[1.054] text-purple-04 hover:underline";

  // Animate bars from width 0 when the chart enters the viewport;
  // skipped under prefers-reduced-motion.
  const chartRef = useRef<HTMLDivElement>(null);
  const reducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const [observed, setObserved] = useState(false);
  const animate = reducedMotion || observed;
  useEffect(() => {
    if (reducedMotion) return;
    const node = chartRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setObserved(true);
            observer.disconnect();
            return;
          }
        }
      },
      { threshold: 0.25 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [reducedMotion]);

  return (
    <section {...storyblokEditable(blok)} className={bg}>
      <div className="mx-auto w-full max-w-[1280px] px-[clamp(1.25rem,4vw,2.5rem)] py-10 lg:py-[80px]">
        <div
          className={`flex flex-col gap-10 lg:items-center lg:gap-[80px] ${
            chartOnLeft ? "lg:flex-row-reverse" : "lg:flex-row"
          }`}
        >
          <div className="flex flex-col gap-6 lg:w-[501px] lg:shrink-0 lg:gap-8">
            <div className="flex flex-col gap-3 lg:gap-5">
              {blok.title && (
                <h2 className="text-purple-05 text-[clamp(2.25rem,4vw,3rem)] leading-[1.08] font-bold">
                  {blok.title}
                </h2>
              )}
              {blok.description && (
                <p className="text-body lg:text-h6 leading-[1.64] text-black lg:leading-[1.52]">
                  {blok.description}
                </p>
              )}
            </div>

            {showButton &&
              (action ? (
                <button
                  type="button"
                  onClick={(e) => triggerAction(action, e)}
                  className={`cursor-pointer ${buttonClass}`}
                >
                  {blok.button_label}
                  <ArrowIcon />
                </button>
              ) : (
                <a
                  href={link.href}
                  {...(link.target ? { target: link.target } : {})}
                  className={buttonClass}
                >
                  {blok.button_label}
                  <ArrowIcon />
                </a>
              ))}
          </div>

          {rows.length > 0 && (
            <div ref={chartRef} className="w-full lg:flex-1">
              <ul className="flex flex-col gap-3 lg:gap-4">
                {rows.map((row, i) => {
                  const baseline = parseNum(row.baseline_value);
                  const bar = parseNum(row.bar_value);
                  const baselinePct = (baseline / scale) * 100;
                  const barPct = (bar / scale) * 100;
                  // Stagger so bars sweep in one after the next.
                  const baseDelay = i * 120;
                  return (
                    <li
                      key={row._uid as string}
                      {...storyblokEditable(row)}
                      className="grid grid-cols-[56px_1fr] items-center gap-3 md:grid-cols-[80px_1fr] md:gap-4"
                    >
                      <span className="text-[12px] md:text-button leading-tight text-black">
                        {row.label}
                      </span>
                      <div className="relative h-7 md:h-10">
                        {bar > 0 && (
                          <div
                            className={`absolute top-0 left-0 h-full min-w-[60px] overflow-hidden rounded-full transition-[width] duration-1100 ease-out md:min-w-[12%] ${barBgClass}`}
                            style={{
                              width: animate ? `${barPct}%` : "0%",
                              transitionDelay: `${baseDelay + 120}ms`,
                            }}
                          />
                        )}
                        {baseline > 0 && (
                          <div
                            className="absolute top-0 left-0 flex h-full min-w-[60px] items-center justify-end overflow-hidden rounded-full bg-[#231f20] pr-3 transition-[width] duration-900 ease-out md:min-w-[12%]"
                            style={{
                              width: animate ? `${baselinePct}%` : "0%",
                              transitionDelay: `${baseDelay}ms`,
                            }}
                          >
                            <span className="md:text-body lg:text-body text-[11px] text-white">
                              {format(baseline, row.baseline_suffix)}
                            </span>
                          </div>
                        )}
                        {/* Sits at the bar's right edge, above both bars so a
                            short tail can't hide it. */}
                        {bar > 0 && (
                          <span
                            className="absolute top-1/2 z-10 hidden -translate-x-full -translate-y-1/2 pr-3 text-body font-bold whitespace-nowrap text-white transition-[left] duration-1100 ease-out md:block"
                            style={{
                              left: animate ? `max(12%, ${barPct}%)` : "12%",
                              transitionDelay: `${baseDelay + 120}ms`,
                            }}
                          >
                            {format(bar, row.bar_suffix)}
                          </span>
                        )}
                        {bar > 0 && (
                          <span
                            className="absolute top-1/2 ml-2 -translate-y-1/2 text-[11px] font-bold whitespace-nowrap text-black md:hidden"
                            style={{
                              left: `max(60px, ${baselinePct}%, ${barPct}%)`,
                            }}
                          >
                            {format(bar, row.bar_suffix)}
                          </span>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>

              <div className="mt-6 flex flex-col gap-2 md:mt-8">
                <ul className="flex flex-wrap items-center gap-x-6 gap-y-2">
                  <li className="text-[12px] md:text-button flex items-center gap-2 text-black/70">
                    <span aria-hidden className="size-3 shrink-0 rounded-full bg-[#231f20]" />
                    {baselineLabel}
                  </li>
                  <li className="text-[12px] md:text-button flex items-center gap-2 text-black/70">
                    <span aria-hidden className={`size-3 shrink-0 rounded-full ${barBgClass}`} />
                    {barLabel}
                  </li>
                </ul>
                {footnote && (
                  <p className="text-[12px] max-w-[640px] leading-normal text-black/55">
                    {footnote}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
